import React from "react";
import { FiUser, FiActivity, FiBarChart2, FiCamera, FiTarget, FiZap, FiMoon } from "react-icons/fi";
import { FaDumbbell } from "react-icons/fa";
import { Avaliation } from "../../types";
import {
  CollapsibleSection,
  FormInput,
  PhotosSection,
} from "./AvaliacaoFormComponents";
import { IconRating } from "./IconRating";

/**
 * Seções do formulário de avaliação
 */

type CollapsedSections = {
  dadosGerais: boolean;
  dobrasCutaneas: boolean;
  bioimpedancia: boolean;
  fotos: boolean;
  informacoesAdicionais: boolean;
};

interface FormSectionProps {
  form: Avaliation;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImagesChange?: (images: string[]) => void;
  collapsed: CollapsedSections;
  onToggleSection: (section: keyof CollapsedSections) => void;
}

export function DadosGeraisSection({
  form,
  onChange,
  collapsed,
  onToggleSection,
}: FormSectionProps) {
  return (
    <CollapsibleSection
      title="Dados Gerais"
      description="Informações básicas e medidas corporais."
      icon={FiUser}
      iconBgColor="bg-purple-100 dark:bg-purple-900/30"
      iconColor="text-purple-600 dark:text-purple-400"
      isCollapsed={collapsed.dadosGerais}
      onToggle={() => onToggleSection("dadosGerais")}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 md:gap-5 lg:grid-cols-3">
          <FormInput
            label="Peso (kg)"
            name="peso"
            value={form?.peso || ""}
            onChange={onChange}
            placeholder="75.5"
          />
          <FormInput
            label="Altura (cm)"
            name="altura"
            value={form.altura || ""}
            onChange={onChange}
            placeholder="175"
          />
        </div>

        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Lado esquerdo
          </h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 md:gap-5 lg:grid-cols-4">
            <FormInput
              label="Braço Esquerdo (cm)"
              name="braco_esquerdo"
              value={form.braco_esquerdo || ""}
              onChange={onChange}
              placeholder="35"
            />
            <FormInput
              label="Braço Esq. Contraído (cm)"
              name="braco_esquerdo_contraido"
              value={form.braco_esquerdo_contraido || ""}
              onChange={onChange}
              placeholder="38"
            />
            <FormInput
              label="Coxa Esquerda (cm)"
              name="coxa_esquerda"
              value={form.coxa_esquerda || ""}
              onChange={onChange}
              placeholder="55"
            />
            <FormInput
              label="Perna Esquerda (cm)"
              name="perna_esquerda"
              value={form.perna_esquerda || ""}
              onChange={onChange}
              placeholder="38"
            />
          </div>
        </div>

        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Tronco
          </h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 md:gap-5 lg:grid-cols-4">
            <FormInput
              label="Tórax (cm)"
              name="torax"
              value={form.torax || ""}
              onChange={onChange}
              placeholder="95"
            />
            <FormInput
              label="Cintura (cm)"
              name="cintura"
              value={form.cintura || ""}
              onChange={onChange}
              placeholder="80"
            />
            <FormInput
              label="Abdômen (cm)"
              name="abdomen"
              value={form.abdomen || ""}
              onChange={onChange}
              placeholder="85"
            />
            <FormInput
              label="Quadril (cm)"
              name="quadril"
              value={form.quadril || ""}
              onChange={onChange}
              placeholder="90"
            />
          </div>
        </div>

        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Lado direito
          </h4>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 md:gap-5 lg:grid-cols-4">
            <FormInput
              label="Braço Direito (cm)"
              name="braco_direito"
              value={form.braco_direito || ""}
              onChange={onChange}
              placeholder="35"
            />
            <FormInput
              label="Braço Dir. Contraído (cm)"
              name="braco_direito_contraido"
              value={form.braco_direito_contraido || ""}
              onChange={onChange}
              placeholder="38"
            />
            <FormInput
              label="Coxa Direita (cm)"
              name="coxa_direita"
              value={form.coxa_direita || ""}
              onChange={onChange}
              placeholder="55"
            />
            <FormInput
              label="Perna Direita (cm)"
              name="perna_direita"
              value={form.perna_direita || ""}
              onChange={onChange}
              placeholder="38"
            />
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
}

export function DobrasCutaneasSection({
  form,
  onChange,
  collapsed,
  onToggleSection,
}: FormSectionProps) {
  return (
    <CollapsibleSection
      title="Dobras Cutâneas"
      description="Medidas de espessura de dobras cutâneas"
      icon={FiActivity}
      iconBgColor="bg-blue-100 dark:bg-blue-900/30"
      iconColor="text-blue-600 dark:text-blue-400"
      isCollapsed={collapsed.dobrasCutaneas}
      onToggle={() => onToggleSection("dobrasCutaneas")}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 md:gap-5 lg:grid-cols-4">
        <FormInput
          label="Tricipal (mm)"
          name="dobra_cutanea.tricipal"
          value={form.dobra_cutanea.tricipal || ""}
          onChange={onChange}
          placeholder="15"
        />
        <FormInput
          label="Subescapular (mm)"
          name="dobra_cutanea.subescapular"
          value={form.dobra_cutanea.subescapular || ""}
          onChange={onChange}
          placeholder="12"
        />
        <FormInput
          label="Peitoral (mm)"
          name="dobra_cutanea.peitoral"
          value={
            form.dobra_cutanea.peitoral || form.dobra_cutanea.biceps || ""
          }
          onChange={onChange}
          placeholder="8"
        />
        <FormInput
          label="Bíceps (mm)"
          name="dobra_cutanea.biceps"
          value={form.dobra_cutanea.biceps || ""}
          onChange={onChange}
          placeholder="8"
        />
        <FormInput
          label="Supraíliaca (mm)"
          name="dobra_cutanea.suprailiaca"
          value={form.dobra_cutanea.suprailiaca || ""}
          onChange={onChange}
          placeholder="18"
        />
        <FormInput
          label="Axilar Média (mm)"
          name="dobra_cutanea.axilar_media"
          value={form.dobra_cutanea.axilar_media || ""}
          onChange={onChange}
          placeholder="14"
        />
        <FormInput
          label="Abdominal (mm)"
          name="dobra_cutanea.abdominal"
          value={form.dobra_cutanea.abdominal || ""}
          onChange={onChange}
          placeholder="22"
        />
        <FormInput
          label="Coxa (mm)"
          name="dobra_cutanea.coxa"
          value={form.dobra_cutanea.coxa || ""}
          onChange={onChange}
          placeholder="20"
        />
        <FormInput
          label="Panturrilha (mm)"
          name="dobra_cutanea.panturrilha"
          value={form.dobra_cutanea.panturrilha || ""}
          onChange={onChange}
          placeholder="10"
        />
      </div>
    </CollapsibleSection>
  );
}

export function BioimpedanciaSection({
  form,
  onChange,
  collapsed,
  onToggleSection,
}: FormSectionProps) {
  return (
    <CollapsibleSection
      title="Bioimpedância"
      description="Composição corporal detalhada."
      icon={FiBarChart2}
      iconBgColor="bg-green-100 dark:bg-green-900/30"
      iconColor="text-green-600 dark:text-green-400"
      isCollapsed={collapsed.bioimpedancia}
      onToggle={() => onToggleSection("bioimpedancia")}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 md:gap-5 lg:grid-cols-3">
        <FormInput
          label="% Gordura"
          name="biopedancia.percentual_gordura"
          value={form.biopedancia.percentual_gordura || ""}
          onChange={onChange}
          placeholder="18.5"
        />
        <FormInput
          label="% Músculo"
          name="biopedancia.percentual_musculo"
          value={form.biopedancia.percentual_musculo || ""}
          onChange={onChange}
          placeholder="42.3"
        />
        <FormInput
          label="Idade Metabólica"
          name="idade_metabolica"
          value={form.idade_metabolica || ""}
          onChange={onChange}
          placeholder="28"
        />
        <FormInput
          label="Taxa Metabólica"
          name="taxa_metabolica"
          value={form.taxa_metabolica || ""}
          onChange={onChange}
          placeholder="1650"
        />
        <FormInput
          label="Índice Gordura Visceral"
          name="indice_de_gordura_visceral"
          value={form.indice_de_gordura_visceral || ""}
          onChange={onChange}
          placeholder="8"
        />
        <FormInput
          label="Gordura Visceral (kg)"
          name="gordura_visceral"
          value={form.gordura_visceral || ""}
          onChange={onChange}
          placeholder="2.5"
        />
      </div>
    </CollapsibleSection>
  );
}

export function FotosSection({
  form,
  onImagesChange,
  collapsed,
  onToggleSection,
}: FormSectionProps) {
  if (!onImagesChange) {
    return null;
  }

  return (
    <CollapsibleSection
      title="Fotos"
      description="Imagens da avaliação física"
      icon={FiCamera}
      iconBgColor="bg-orange-100 dark:bg-orange-900/30"
      iconColor="text-orange-600 dark:text-orange-400"
      isCollapsed={collapsed.fotos}
      onToggle={() => onToggleSection("fotos")}
    >
      <PhotosSection
        images={form.images || []}
        onImagesChange={onImagesChange}
      />
    </CollapsibleSection>
  );
}

export function InformacoesAdicionaisSection({
  form,
  onChange,
  collapsed,
  onToggleSection,
}: FormSectionProps) {
  const handleStarChange = (field: "motivacao" | "sono" | "frequencia_treino", value: number) => {
    const event = {
      target: {
        name: field,
        value: value.toString(),
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(event);
  };

  return (
    <CollapsibleSection
      title="Informações Adicionais"
      description="Motivação, sono e frequência de treino"
      icon={FiTarget}
      iconBgColor="bg-indigo-100 dark:bg-indigo-900/30"
      iconColor="text-indigo-600 dark:text-indigo-400"
      isCollapsed={collapsed.informacoesAdicionais}
      onToggle={() => onToggleSection("informacoesAdicionais")}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:gap-5 lg:grid-cols-3">
        <IconRating
          label="Motivação"
          value={form.motivacao || 0}
          onChange={(value) => handleStarChange("motivacao", value)}
          maxValue={5}
          Icon={FiZap}
          filledClassName="fill-orange-400 text-orange-400"
          emptyClassName="text-gray-300 dark:text-gray-600"
        />
        <IconRating
          label="Qualidade do Sono"
          value={form.sono || 0}
          onChange={(value) => handleStarChange("sono", value)}
          maxValue={5}
          Icon={FiMoon}
          filledClassName="fill-blue-400 text-blue-400"
          emptyClassName="text-gray-300 dark:text-gray-600"
        />
        <IconRating
          label="Frequência de Treino"
          value={form.frequencia_treino || 0}
          onChange={(value) => handleStarChange("frequencia_treino", value)}
          maxValue={7}
          Icon={FaDumbbell}
          filledClassName="fill-green-400 text-green-400"
          emptyClassName="text-gray-300 dark:text-gray-600"
        />
      </div>
    </CollapsibleSection>
  );
}
